// Patient Risk Factor Service
// Manage library of patient-specific risk factors

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';

class PatientRiskFactorService {
  constructor() {
    this.factorsCollection = collection(db, 'patient_risk_factors');
  }
  
  /**
   * Create a new patient risk factor
   */
  async createFactor(factorData, userId) {
    const now = Timestamp.now();
    
    const factor = {
      ...factorData,
      multiplier: parseFloat(factorData.multiplier) || 1.0,
      isActive: factorData.isActive !== undefined ? factorData.isActive : true,
      applicableConditions: factorData.applicableConditions || [],
      departmentSpecific: factorData.departmentSpecific || [],
      createdAt: now,
      createdBy: userId,
      updatedAt: now,
      updatedBy: userId
    };
    
    // Validate multiplier range
    if (factor.multiplier < 1.0 || factor.multiplier > 3.0) {
      throw new Error('Patient risk factor multiplier must be between 1.0 and 3.0');
    }
    
    // Remove id field if it exists
    delete factor.id;
    
    const docRef = await addDoc(this.factorsCollection, factor);
    
    console.log(`✅ Created patient risk factor: ${docRef.id}`);
    return docRef.id;
  }
  
  /**
   * Get factor by ID
   */
  async getFactor(factorId) {
    const docRef = doc(this.factorsCollection, factorId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  }
  
  /**
   * Update factor
   */
  async updateFactor(factorId, updates, userId) {
    const docRef = doc(this.factorsCollection, factorId);
    
    // Validate multiplier if being updated
    if (updates.multiplier !== undefined) {
      const multiplier = parseFloat(updates.multiplier);
      if (multiplier < 1.0 || multiplier > 3.0) {
        throw new Error('Patient risk factor multiplier must be between 1.0 and 3.0');
      }
      updates.multiplier = multiplier;
    }
    
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now(),
      updatedBy: userId
    });
    
    console.log(`✅ Updated patient risk factor: ${factorId}`);
  }
  
  /**
   * Delete factor
   */
  async deleteFactor(factorId) {
    const docRef = doc(this.factorsCollection, factorId);
    await deleteDoc(docRef);
    
    console.log(`✅ Deleted patient risk factor: ${factorId}`);
  }
  
  /**
   * Get all factors
   */
  async getAllFactors() {
    const q = query(
      this.factorsCollection,
      orderBy('name', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  /**
   * Get active factors only
   */
  async getActiveFactors() {
    const q = query(
      this.factorsCollection,
      where('isActive', '==', true),
      orderBy('name', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  /**
   * Get factors by category
   */
  async getFactorsByCategory(category) {
    const q = query(
      this.factorsCollection,
      where('category', '==', category),
      where('isActive', '==', true),
      orderBy('name', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  /**
   * Get factors applicable to a department
   */
  async getFactorsByDepartment(departmentId) {
    // Get all active factors and filter client-side
    const allFactors = await this.getActiveFactors();
    
    return allFactors.filter(factor => 
      !factor.departmentSpecific || 
      factor.departmentSpecific.length === 0 ||
      factor.departmentSpecific.includes(departmentId)
    );
  }
  
  /**
   * Get factors applicable to specific conditions
   */
  async getFactorsByConditions(conditions) {
    if (!conditions || conditions.length === 0) {
      return [];
    }
    
    // Get all active factors and filter client-side
    const allFactors = await this.getActiveFactors();
    
    return allFactors.filter(factor => {
      if (!factor.applicableConditions || factor.applicableConditions.length === 0) {
        return false;
      }
      
      // Check if any of the patient's conditions match the factor's applicable conditions
      return factor.applicableConditions.some(condition => 
        conditions.includes(condition)
      );
    });
  }
  
  /**
   * Get factors applicable to an age range
   */
  async getFactorsByAgeRange(ageRange) {
    // Get all active factors
    const allFactors = await this.getActiveFactors();
    
    return allFactors.filter(factor => {
      if (!factor.ageRange) {
        return true; // Factor applies to all ages
      }
      
      // Parse age range (e.g., '<18', '18-65', '>65')
      if (ageRange === '<18' && factor.ageRange.max && factor.ageRange.max <= 18) {
        return true;
      }
      if (ageRange === '18-65' && factor.ageRange.min >= 18 && factor.ageRange.max <= 65) {
        return true;
      }
      if (ageRange === '>65' && factor.ageRange.min && factor.ageRange.min >= 65) {
        return true;
      }
      
      return false;
    });
  }
  
  /**
   * Get high-impact factors (multiplier > 2.0)
   */
  async getHighImpactFactors() {
    const allFactors = await this.getActiveFactors();
    
    return allFactors.filter(factor => factor.multiplier > 2.0)
      .sort((a, b) => b.multiplier - a.multiplier);
  }
  
  /**
   * Calculate total multiplier for a set of factor IDs
   */
  async calculateTotalMultiplier(factorIds) {
    if (!factorIds || factorIds.length === 0) {
      return 1.0;
    }
    
    const factors = await Promise.all(
      factorIds.map(id => this.getFactor(id))
    );
    
    let totalMultiplier = 1.0;
    for (const factor of factors) {
      if (factor && factor.isActive) {
        totalMultiplier *= factor.multiplier;
      }
    }
    
    // Cap at 3.0
    return Math.min(totalMultiplier, 3.0);
  }
  
  /**
   * Get factor statistics
   */
  async getFactorStatistics() {
    const allFactors = await this.getAllFactors();
    const activeFactors = allFactors.filter(f => f.isActive);
    
    const byCategory = {
      'Demographic': activeFactors.filter(f => f.category === 'Demographic').length,
      'Clinical': activeFactors.filter(f => f.category === 'Clinical').length,
      'Procedural': activeFactors.filter(f => f.category === 'Procedural').length,
      'Comorbidity': activeFactors.filter(f => f.category === 'Comorbidity').length
    };
    
    const avgMultiplier = activeFactors.length > 0
      ? activeFactors.reduce((sum, f) => sum + f.multiplier, 0) / activeFactors.length
      : 1.0;
    
    return {
      total: allFactors.length,
      active: activeFactors.length,
      inactive: allFactors.length - activeFactors.length,
      byCategory,
      avgMultiplier: avgMultiplier.toFixed(2),
      highImpact: activeFactors.filter(f => f.multiplier > 2.0).length
    };
  }
  
  /**
   * Toggle factor active status
   */
  async toggleActive(factorId, userId) {
    const factor = await this.getFactor(factorId);
    if (!factor) throw new Error('Factor not found');
    
    await this.updateFactor(factorId, {
      isActive: !factor.isActive
    }, userId);
    
    console.log(`✅ Toggled active status for factor: ${factorId}`);
  }
  
  /**
   * Batch get factors by IDs
   */
  async getFactorsByIds(factorIds) {
    if (!factorIds || factorIds.length === 0) {
      return [];
    }
    
    const factors = await Promise.all(
      factorIds.map(id => this.getFactor(id))
    );
    
    return factors.filter(f => f !== null);
  }
}

const patientRiskFactorService = new PatientRiskFactorService();

export { PatientRiskFactorService, patientRiskFactorService };